import R from "react"
import {Emit_main} from "../navigation"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {Link, useNavigate} from "react-router-dom"
import {faFacebook, faLinkedin, faTiktok, faXTwitter} from "@fortawesome/free-brands-svg-icons"
import {faPlus, faPencil, faTrash, IconDefinition} from "@fortawesome/free-solid-svg-icons"

import {Eres_card, Eres_iconbutton, Eres_info_badge, Eres_warning_badge,
    Eres_danger_badge, Eres_header2xl,Eres_iconbutton_link} from "../controls"

import * as P from "../program-state"
import * as C from "../controls"
import {z} from "zod"


const headers = C.eres_prepare_table([
    ["name"         , "Brand Name (url)" , "50%" , C.nosort],
    ["network"      , "Networks"         , "25%" , C.nosort],
    ["created_at"   , "Added"            , "12%" , C.nosort],
    ["quickbuttons" , " "                , "1%"  , C.nosort],
])

const used_networks = (brand : P.brand) => {
    let networks : P.network_set = P.empty_network_set
    if (brand.linkedin_org && brand.linkedin_ad_account) networks = P.network_set_set(networks, "nw-linkedin")
    if (brand.meta_page && brand.ad_account_id) networks = P.network_set_set(networks, "nw-facebook")
    if (brand.tiktok_identity_id && brand.tiktok_ad_account_id) networks = P.network_set_set(networks, "nw-tiktok")
    if (brand.x_ad_account_id) networks = P.network_set_set(networks, "nw-x")
    return networks
}

const network_badges : Record<P.connected_network_tag, [IconDefinition, string]> = {
    "nw-facebook" : [faFacebook, "Facebook"],
    "nw-linkedin" : [faLinkedin, "Linkedin"],
    "nw-tiktok"   : [faTiktok, "Tiktok"],
    "nw-x"        : [faXTwitter, "X"],
}

export const Emit_active_networks_icon = (brand : P.brand) => {
    const un = used_networks(brand);
    if (un === 0) return <Eres_warning_badge>None</Eres_warning_badge>

    return (
        <div className="tw-flex tw-justify-left">
        <div className="tw-flex tw-justify-left tw-space-x-3">
        {Object.entries(network_badges).map(([tag, [icon, label]]) => {
            const network_tag = tag as P.connected_network_tag
            if ((un & P.network_bits[network_tag]) !== 0) {
                return <div key={tag}><FontAwesomeIcon icon={icon} aria-hidden="true" title={label}/></div>
            }
            return null
        })}
        </div>
        </div>
    )
}

export const Brand_table = ({brands, onDeleteBrand} : {brands : P.brand[] | undefined, onDeleteBrand : (item : P.brand) => Promise<void>}) => {
    const navigate = useNavigate()
    const go_to_brand = R.useCallback((item : P.brand) => () => navigate(`/brands/${item.id}`), [navigate])
    
    const delete_brand = R.useCallback((item : P.brand) => async (e : R.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!confirm(`Are you sure you want to delete the brand "${item.name}"? This action cannot be undone.`)) {
            return
        }
        
        await onDeleteBrand(item)
    }, [onDeleteBrand])

    let table_body : R.ReactElement[] | null = null
    let status_text : string = "Fetching..."
    if (brands !== undefined) {
        table_body = brands.map((item) =>
            <tr key={item.id} className="tw-border-y tw-border-x-0 last:tw-border-b-0 hover:tw-bg-gray-50 hover:tw-cursor-pointer" onClick={go_to_brand(item)}>
                <C.Eres_basic_tablecol>
                    <div className="tw-text-base tw-text-big-stone-950 tw-font-semibold">{item.name}</div>
                    <p className="tw-text-xs">{item.url}</p>
                </C.Eres_basic_tablecol>
                <C.Eres_basic_tablecol>
                    {Emit_active_networks_icon(item)}
                </C.Eres_basic_tablecol>
                <C.Eres_basic_tablecol>
                    4/10/2023
                </C.Eres_basic_tablecol>
                <C.Eres_basic_tablecol align="right">
                    <Link to={`/brands/${item.id}`} onClick={(e) => e.stopPropagation()}><FontAwesomeIcon icon={faPencil} aria-hidden="true" className="tw-pe-1.5"/></Link>
                    <button type="button" onClick={delete_brand(item)} className="tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer"><FontAwesomeIcon icon={faTrash} aria-hidden="true"/></button>
                </C.Eres_basic_tablecol>
            </tr>)
        status_text = `${brands.length} ${brands.length === 1 ? "entry" : "entries"}`
    }

    return (
        <C.Eres_basic_table headers={headers.headers} footer={status_text}>
            {table_body}
        </C.Eres_basic_table>
    )

}

export const Brand_list_display = ({}) => {
    P.run_once_on_component_creation(() => void P.poll_connected_brands(ps, cs))
    const [brands, ps, cs] = P.use_brands()
    
    const handleDeleteBrand = R.useCallback(async (item : P.brand) => {
        try {
            const [ok, resp] = await P.issue_json_delete(P.v1api`brands/${item.id}`, z.any(), cs.token)
            if (ok) {
                // force immediate refresh 
                const writableCs = cs as any
                writableCs.brands_last_update = 0
                void P.poll_connected_brands(ps, cs)
            } else {
                console.error('Delete brand failed:', resp)
                
                // try to extract error message 
                let errorMessage = 'Failed to delete brand. Please try again.'
                if (resp instanceof P.http_error) {
                    try {
                        const errorData = await resp.resp.json()
                        if (errorData.detail && errorData.detail.message) {
                            errorMessage = errorData.detail.message
                        }
                    } catch (parseError) {
                        console.error('Could not parse error response:', parseError)
                    }
                }
                
                alert(errorMessage)
            }
        } catch (error) {
            console.error('Error deleting brand:', error)
            alert('Failed to delete brand. Please try again.')
        }
    }, [ps, cs])

    return (<>
    <Eres_card>
    <div className="tw-mt-6 tw-px-6 tw-flex tw-flex-wrap">
        <div className="tw-flex-grow">
            <Eres_header2xl>Brands</Eres_header2xl>
        </div>
        <div className="tw-flex tw-flex-grow-0">
            <Eres_iconbutton_link href="/brands/create" icon={faPlus}>Create Brand</Eres_iconbutton_link>
        </div>
    </div>

    <div className="tw-mt-3">
        <Brand_table brands={brands} onDeleteBrand={handleDeleteBrand}/>
    </div>
    </Eres_card>
    </>)
}

export default function Brand() {
    return (<Emit_main page_id="PAGE-brands"><Brand_list_display /></Emit_main>)
}
